import { useCallback, useEffect, useRef } from 'react'
import { PatientDocument } from 'common/enum/patient-document'
import { UploadService } from 'service/usecases/upload-service'
import { UserContext } from 'domain/entities/upload-document'
import { UPLOAD_CONFIG } from 'common/utils/upload-config'
import { getUploadLogger } from 'common/utils/upload-logger'
import { useDocumentUpload } from './useDocumentUpload'

const logger = getUploadLogger()

export type PollOptions = {
  /**
   * Realiza uma tentativa de polling — busca o estado atualizado do recurso e
   * verifica se o documento já foi processado pelo backend.
   *
   * Retornar `true` interrompe o loop com sucesso. O caller é responsável por
   * atualizar o estado da UI (setState/store) ao detectar o documento.
   */
  pollOnce: (params: {
    documentId: string
    fileName: string
    type: string
  }) => Promise<boolean>

  /** Chamado se todas as tentativas falharem (documento não processado a tempo) */
  onTimeout?: () => void
}

export type UploadAndPollContext = {
  surgicalOrderId: number
  fileName: string
  documentType: string
}

export type UploadAndPollResult = {
  documentId: string | null
  pollingCompleted: boolean
  documentFound: boolean
}

export type UploadOnlyResult = {
  documentId: string | null
  success: boolean
}

/**
 * Hook compartilhado para upload + polling de processamento de documento.
 *
 * Encapsula:
 * - Upload via `useDocumentUpload` (S3 + notificação do backend)
 * - Polling com `setTimeout` e cleanup automático em unmount
 * - Configuração centralizada em `UPLOAD_CONFIG`
 * - Logging padronizado de timeout/erro
 *
 * A Promise retornada só resolve após o polling terminar (sucesso ou timeout),
 * permitindo que o caller mantenha o loading até o processo completo finalizar.
 */
export const useDocumentUploadAndPolling = (uploadService: UploadService) => {
  const { uploadDocument, resetState, uploadState, cancelUpload } =
    useDocumentUpload(uploadService)

  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current)
        pollingTimerRef.current = null
      }
    }
  }, [])

  const uploadAndPoll = useCallback(
    async (
      file: File,
      surgicalOrderId: number,
      type: PatientDocument,
      user: UserContext,
      options: PollOptions
    ): Promise<UploadAndPollResult> => {
      resetState()

      const documentId = await uploadDocument(file, surgicalOrderId, type, user)

      if (!documentId) {
        return {
          documentId: null,
          pollingCompleted: false,
          documentFound: false
        }
      }

      const context: UploadAndPollContext = {
        surgicalOrderId,
        fileName: file.name,
        documentType: type
      }

      const pollingResult = await new Promise<{
        documentFound: boolean
        pollingCompleted: boolean
      }>((resolve) => {
        pollingTimerRef.current = setTimeout(async () => {
          try {
            for (
              let attempt = 1;
              attempt <= UPLOAD_CONFIG.POLLING_MAX_ATTEMPTS;
              attempt++
            ) {
              if (!isMountedRef.current) {
                resolve({ documentFound: false, pollingCompleted: false })
                return
              }

              const found = await options.pollOnce({
                documentId,
                fileName: file.name,
                type
              })

              if (found) {
                resolve({ documentFound: true, pollingCompleted: true })
                return
              }

              if (attempt < UPLOAD_CONFIG.POLLING_MAX_ATTEMPTS) {
                await new Promise((r) =>
                  setTimeout(r, UPLOAD_CONFIG.POLLING_INTERVAL_MS)
                )
              }
            }

            if (!isMountedRef.current) {
              resolve({ documentFound: false, pollingCompleted: false })
              return
            }

            logger.warn('Upload timeout: documento ainda não processado', {
              ...context,
              documentId,
              attempts: UPLOAD_CONFIG.POLLING_MAX_ATTEMPTS
            })
            options.onTimeout?.()
            resolve({ documentFound: false, pollingCompleted: true })
          } catch (error) {
            if (!isMountedRef.current) {
              resolve({ documentFound: false, pollingCompleted: false })
              return
            }
            logger.error(
              'Erro ao verificar processamento do documento',
              error,
              {
                ...context,
                documentId
              }
            )
            resolve({ documentFound: false, pollingCompleted: true })
          }
        }, UPLOAD_CONFIG.POLLING_INITIAL_DELAY_MS)
      })

      return {
        documentId,
        ...pollingResult
      }
    },
    [uploadDocument, resetState]
  )

  /**
   * Faz apenas o upload (S3 + notificação ao backend) sem polling.
   * Útil para dar feedback rápido ao usuário e verificar processamento depois.
   *
   * @returns documentId se sucesso, null se falha
   */
  const uploadOnly = useCallback(
    async (
      file: File,
      surgicalOrderId: number,
      type: PatientDocument,
      user: UserContext
    ): Promise<UploadOnlyResult> => {
      resetState()

      const documentId = await uploadDocument(file, surgicalOrderId, type, user)

      if (!documentId) {
        return {
          documentId: null,
          success: false
        }
      }

      return {
        documentId,
        success: true
      }
    },
    [uploadDocument, resetState]
  )

  return {
    uploadAndPoll,
    uploadOnly,
    uploadState,
    cancelUpload,
    resetState
  }
}
